// pages/PaymentDetailsPage.tsx
import { useNavigate } from "react-router-dom";
import { ArrowLeft, CreditCard, User, Mail, Phone, Hash, Receipt, CheckCircle, XCircle, Clock, Loader2 } from "lucide-react";
import PageLayout from "../../../layouts/PageLayout";
import useCustomParams from "../../../hooks/params/useCustomParams";
import { alertSuccess } from "../../../helpers/alertError";
import { usePayment } from "../../../hooks/payments/usePayment";

export default function PaymentDetailsPage() {
  const navigate = useNavigate();
  const { id } = useCustomParams();
  const { payment, loading } = usePayment({ id: id! });

  if (loading) {
    return (
      <PageLayout title="Détails du paiement">
        <div className="flex justify-center py-20">
          <Loader2 className="animate-spin h-12 w-12 text-primary" />
        </div>
      </PageLayout>
    );
  }

  if (!payment) {
    return (
      <PageLayout title="Détails du paiement">
        <div className="text-center py-20 text-gray-500">Paiement non trouvé</div>
      </PageLayout>
    );
  }

  const getStatusBadge = (status: string) => {
    const statusMap = {
      pending: { label: "En attente", className: "bg-yellow-100 text-yellow-700" },
      approved: { label: "Approuvé", className: "bg-green-100 text-green-700" },
      canceled: { label: "Annulé", className: "bg-red-100 text-red-700" }
    };
    const s = statusMap[status as keyof typeof statusMap] || statusMap.pending;
    return <span className={`px-2 py-1 rounded-full text-xs font-medium ${s.className}`}>{s.label}</span>;
  };

  const formatDate = (dateString: string | null) => {
    if (!dateString) return "-";

    const date = new Date(dateString)

    if (!date) return "-";
    return new Date(date).toLocaleDateString('fr-FR', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  return (
    <PageLayout
      title="Détails du paiement"
      actions={
        <button
          onClick={() => navigate(-1)}
          className="flex items-center gap-2 px-4 py-2 bg-gray-600 text-white rounded-lg hover:bg-gray-700 transition"
        >
          <ArrowLeft size={18} /> Retour
        </button>
      }
    >
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Informations principales */}
        <div className="lg:col-span-2 space-y-6">
          {/* En-tête */}
          <div className="bg-white rounded-xl shadow-sm border p-6">
            <div className="flex flex-wrap items-center justify-between gap-4">
              <div>
                <h2 className="text-xl font-bold">{payment.reference}</h2>
                <p className="text-sm text-gray-500 flex items-center gap-2 mt-1">
                  <Hash size={14} />
                  Transaction: {payment.transactionId}
                </p>
              </div>
              <div className="flex items-center gap-3">
                {getStatusBadge(payment.status)}
                <span className="text-2xl font-bold text-primary">
                  {payment.amount.toLocaleString('fr-FR')} {payment.currency}
                </span>
              </div>
            </div>
          </div>

          {/* Reçu */}
          {payment.receiptText && (
            <div className="bg-white rounded-xl shadow-sm border p-6">
              <h3 className="text-lg font-semibold mb-4 flex items-center gap-2">
                <Receipt size={18} className="text-primary" />
                Reçu
              </h3>
              <div className="bg-gray-50 rounded-lg p-4 font-mono text-sm whitespace-pre-wrap">
                {payment.receiptText}
              </div>
            </div>
          )}

          {/* Détails du paiement */}
          <div className="bg-white rounded-xl shadow-sm border p-6">
            <h3 className="text-lg font-semibold mb-4 flex items-center gap-2">
              <CreditCard size={18} className="text-primary" />
              Détails du paiement
            </h3>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <p className="text-sm text-gray-500">Méthode</p>
                <p className="font-medium capitalize">{payment.paymentMethod}</p>
              </div>
              <div>
                <p className="text-sm text-gray-500">Commande</p>
                <p className="font-mono">{payment.orderId}</p>
              </div>
              <div>
                <p className="text-sm text-gray-500">Date de paiement</p>
                <p>{formatDate(payment.paidAt)}</p>
              </div>
              <div>
                <p className="text-sm text-gray-500">Créé le</p>
                <p>{formatDate(payment.createdAt)}</p>
              </div>
            </div>
          </div>
        </div>

        {/* Sidebar - Client */}
        <div className="space-y-6">
          <div className="bg-white rounded-xl shadow-sm border p-6">
            <h3 className="text-lg font-semibold mb-4 flex items-center gap-2">
              <User size={18} className="text-primary" />
              Client
            </h3>
            <div className="space-y-3">
              <div className="flex items-center gap-3">
                <User size={16} className="text-gray-400" />
                <span className="font-medium">{payment.customerName}</span>
              </div>
              <div className="flex items-center gap-3">
                <Mail size={16} className="text-gray-400" />
                <a href={`mailto:${payment.customerEmail}`} className="text-sm text-primary hover:underline">
                  {payment.customerEmail}
                </a>
              </div>
              <div className="flex items-center gap-3">
                <Phone size={16} className="text-gray-400" />
                <a href={`tel:${payment.customerPhone}`} className="text-sm text-primary hover:underline">
                  {payment.customerPhone}
                </a>
              </div>
              <div className="flex items-center gap-3 pt-2 border-t">
                <span className="text-sm text-gray-500">ID Utilisateur</span>
                <span className="font-mono text-sm">{payment.userId}</span>
              </div>
            </div>
          </div>

          {/* Actions */}
          {payment.status === "pending" && (
            <div className="bg-white rounded-xl shadow-sm border p-6">
              <h3 className="text-lg font-semibold mb-4 flex items-center gap-2">
                <Clock size={18} className="text-primary" />
                Actions
              </h3>
              <div className="flex flex-col gap-2">
                <button 
                  onClick={() => {
                    // Approuver le paiement
                    alertSuccess("Paiement approuvé");
                  }}
                  className="w-full flex items-center justify-center gap-2 px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 transition"
                >
                  <CheckCircle size={18} /> Approuver le paiement
                </button>
                <button 
                  onClick={() => {
                    // Annuler le paiement
                    alertSuccess("Paiement annulé");
                  }}
                  className="w-full flex items-center justify-center gap-2 px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 transition"
                >
                  <XCircle size={18} /> Annuler le paiement
                </button>
              </div>
            </div>
          )}
        </div>
      </div>
    </PageLayout>
  );
}